import { type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { incomeStatementsQueryKey } from "./financials.keys";
import { renameIncomeStatementViewLabel } from "./utils/patchIncomeStatementView";

export function useRenameIncomeStatementLineItem(accountId: SuretyAccountId) {
	const { patch } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["renameIncomeStatementLineItem", accountId],
		mutationFn: (body: Dtos.Admin.IncomeStatement.RenameLineItem.Request) =>
			patch<void>(
				`/v2/admin/surety/accounts/${accountId}/income-statements/line-items`,
				body,
				"none",
			),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
			);
			queryClient.setQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
				(cached) =>
					cached?.map((statement) =>
						body.incomeStatementIds.includes(statement.id) && statement.view
							? {
									...statement,
									view: renameIncomeStatementViewLabel(
										statement.view,
										body.oldLabel,
										body.newLabel,
									),
								}
							: statement,
					),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(incomeStatementsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (
				queryClient.isMutating({ mutationKey: ["renameIncomeStatementLineItem", accountId] }) <= 1
			) {
				queryClient.invalidateQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			}
		},
	});

	return {
		renameIncomeStatementLineItem: mutation.mutateAsync,
	};
}
